import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const BASE44_APP_URL = 'https://proud-clear-mind-flow.base44.app';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const heroLogoUrl = 'https://media.base44.com/images/public/6a33f776ca9ab1423a01beaf/1e6c5cc29_image.png';

  return (
    <section ref={sectionRef} className="relative overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center justify-center">
      {/* Background image */}
      <img
        src="https://media.base44.com/images/public/6a33f776ca9ab1423a01beaf/eb5a24bc7_image.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-background-50/40" />

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-20 sm:py-32 text-center">
        <div className="reveal-on-scroll animate-fade-in">
          <img
            src={heroLogoUrl}
            alt="ClearMind Club"
            className="w-64 sm:w-80 mx-auto mb-8 rounded-full"
          />
        </div>

        <div className="reveal-on-scroll animate-fade-in animation-delay-100 inline-flex items-center gap-2 bg-accent-500/90 text-foreground-950 text-sm font-label font-medium px-4 py-1.5 rounded-full mb-6">
          <i className="ri-sparkling-line" />
          Your cognitive wellness membership
        </div>

        <h1 className="reveal-on-scroll animate-fade-in animation-delay-200 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-3xl mx-auto leading-tight text-foreground-950">
          Sharpen Your Mind.
          <br />
          <span className="text-primary-500">Simplify Your Wellness.</span>
        </h1>

        <p className="reveal-on-scroll animate-fade-in animation-delay-300 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed text-foreground-700 font-body">
          Daily tips, simple routines, mind training, nootropic reviews, and productivity tools — all in one calm, practical membership club.
        </p>

        <div className="reveal-on-scroll animate-fade-in animation-delay-400 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={BASE44_APP_URL}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 bg-accent-500 text-foreground-950 hover:bg-accent-700 h-12 rounded-lg w-full sm:w-auto text-base px-8"
          >
            Get the App
            <i className="ri-arrow-right-up-line" />
          </a>
          <Link
            to="/community"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-label font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 disabled:pointer-events-none disabled:opacity-50 border border-background-300 bg-transparent hover:bg-background-100 h-12 rounded-lg w-full sm:w-auto text-base px-8 text-foreground-800"
          >
            Join our Community
          </Link>
        </div>

        <p className="reveal-on-scroll animate-fade-in animation-delay-500 text-xs text-foreground-500 mt-4 font-label">
          £5/month &middot; Cancel anytime &middot; Educational content only
        </p>
      </div>
    </section>
  );
}